/**
 * Custom Icon Service
 * Handles uploading, listing, and deleting custom user icons.
 * Includes client-side image compression to optimize storage.
 */

import { getFreshToken } from '../utils/auth';
import { APP_CONFIG } from '../config/app';
import { getActiveWorkspaceId, workspaceApiHeaders } from '../utils/api/workspaceContext';

const API_URL = APP_CONFIG.apiUrl;

// Limits
const MAX_INPUT_FILE_SIZE = 2 * 1024 * 1024; // 2MB input (before compression)
const MAX_COMPRESSED_SIZE = 100 * 1024; // 100KB after compression
const MAX_ICON_DIMENSION = 256; // Max width/height in pixels
const COMPRESSION_QUALITY = 0.85; // WebP quality (0-1)
const ALLOWED_TYPES = ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg'];

/**
 * Auth + workspace room for API requests. Pin the workspace at the start of
 * a multi-step upload so a switch mid-flight cannot file it in the new room.
 */
async function getAuthHeaders(pinnedWorkspaceId) {
  const token = await getFreshToken();
  return {
    Authorization: `Bearer ${token}`,
    ...workspaceApiHeaders(pinnedWorkspaceId),
  };
}

/**
 * Compress and resize an image file using Canvas API
 * This runs entirely in the browser - no server processing needed
 * @param {File} file - The original file
 * @param {number} maxDimension - Maximum width/height
 * @param {number} quality - Compression quality (0-1)
 * @returns {Promise<{blob: Blob, width: number, height: number}>}
 */
async function compressImage(file, maxDimension = MAX_ICON_DIMENSION, quality = COMPRESSION_QUALITY) {
  // SVGs are already vector/small - skip compression
  if (file.type === 'image/svg+xml') {
    return { blob: file, width: 0, height: 0, skipped: true };
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Use high-quality image smoothing for downscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Try WebP first (best compression), fallback to PNG
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ blob, width, height });
          } else {
            // Fallback to PNG if WebP fails
            canvas.toBlob(
              (pngBlob) => {
                if (pngBlob) {
                  resolve({ blob: pngBlob, width, height });
                } else {
                  reject(new Error('Failed to compress image'));
                }
              },
              'image/png'
            );
          }
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
}

/**
 * Validate an image file before upload
 * @param {File} file - The file to validate
 * @returns {Promise<{valid: boolean, error?: string, width?: number, height?: number}>}
 */
export async function validateImageFile(file) {
  // Check file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { 
      valid: false, 
      error: `Invalid file type. Allowed: PNG, SVG, WebP, JPEG` 
    };
  }
  
  // Check input file size (before compression)
  if (file.size > MAX_INPUT_FILE_SIZE) {
    return { 
      valid: false, 
      error: `File too large. Maximum: ${MAX_INPUT_FILE_SIZE / (1024 * 1024)}MB` 
    };
  }
  
  // For SVGs, skip dimension check (they're scalable)
  if (file.type === 'image/svg+xml') {
    return { valid: true, width: 0, height: 0 };
  }
  
  // Get dimensions for raster images
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: true, width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: 'Failed to load image' });
    };
    
    img.src = url;
  });
}

/**
 * Prepare an image for upload by compressing and resizing
 * @param {File} file - The original file
 * @returns {Promise<{file: File, originalSize: number, compressedSize: number}>}
 */
export async function prepareImageForUpload(file) {
  const originalSize = file.size;
  
  // Compress the image
  const { blob, width, height, skipped } = await compressImage(file);
  
  // If SVG or compression was skipped, return original
  if (skipped) {
    return { file, originalSize, compressedSize: originalSize, wasCompressed: false };
  }
  
  // Create a new File from the blob with appropriate extension
  const extension = blob.type === 'image/webp' ? 'webp' : 'png';
  const baseName = file.name.replace(/\.[^/.]+$/, ''); // Remove original extension
  const newFileName = `${baseName}.${extension}`;
  
  const compressedFile = new File([blob], newFileName, { type: blob.type });
  
  return {
    file: compressedFile,
    originalSize,
    compressedSize: compressedFile.size,
    wasCompressed: true,
    dimensions: { width, height },
  };
}

/**
 * Upload a custom icon (with automatic compression)
 * @param {File} file - The image file to upload
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<{iconId: string, publicUrl: string, fileName: string, compression?: object}>}
 */
export async function uploadIcon(file, onProgress = () => {}) {
  const workspaceId = getActiveWorkspaceId();
  const headers = await getAuthHeaders(workspaceId);
  
  // Step 1: Compress the image on the client side
  onProgress(5);
  const prepared = await prepareImageForUpload(file);
  const uploadFile = prepared.file;
  
  // Log compression results for debugging
  if (prepared.wasCompressed) {
    const savings = ((1 - prepared.compressedSize / prepared.originalSize) * 100).toFixed(1);
    console.log(`[IconService] Compressed: ${(prepared.originalSize / 1024).toFixed(1)}KB → ${(prepared.compressedSize / 1024).toFixed(1)}KB (${savings}% smaller)`);
  }
  
  // Step 2: Get presigned upload URL
  onProgress(15);
  
  const urlResponse = await fetch(`${API_URL}/icons/upload-url`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName: uploadFile.name,
      mimeType: uploadFile.type,
      size: uploadFile.size,
    }),
  });
  
  if (!urlResponse.ok) {
    const error = await urlResponse.json();
    throw new Error(error.error || 'Failed to get upload URL');
  }
  
  const { uploadUrl, iconId, r2Key } = await urlResponse.json();
  
  // Step 3: Upload directly to R2
  onProgress(40);
  
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': uploadFile.type,
      'Content-Length': uploadFile.size.toString(),
    },
    body: uploadFile,
  });
  
  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file to storage');
  }
  
  onProgress(85);
  
  // Step 4: Confirm upload
  const confirmResponse = await fetch(`${API_URL}/icons/confirm`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ iconId }),
  });
  
  if (!confirmResponse.ok) {
    const error = await confirmResponse.json();
    throw new Error(error.error || 'Failed to confirm upload');
  }
  
  onProgress(100);
  
  const result = await confirmResponse.json();
  
  // Include compression info in result
  return {
    ...result,
    compression: prepared.wasCompressed ? {
      originalSize: prepared.originalSize,
      compressedSize: prepared.compressedSize,
      dimensions: prepared.dimensions,
    } : null,
  };
}

/**
 * List user's custom icons
 * @returns {Promise<{icons: Array, count: number, limit: number}>}
 */
export async function listIcons() {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/icons`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list icons');
  }
  
  return await response.json();
}

/**
 * Delete a custom icon
 * @param {string} iconId - The icon ID to delete
 * @returns {Promise<void>}
 */
export async function deleteIcon(iconId) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/icons/${iconId}`, {
    method: 'DELETE',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete icon');
  }
}

// Export limits for UI
export const ICON_LIMITS = {
  maxInputFileSize: MAX_INPUT_FILE_SIZE,
  maxCompressedSize: MAX_COMPRESSED_SIZE,
  maxIconDimension: MAX_ICON_DIMENSION,
  allowedTypes: ALLOWED_TYPES,
  maxIconsPerUser: 1000,
};

